/* global common */
import {
  libInit,
  prefetchImageFromSource,
  sendGoogleEvent,
  isObjectFitSupported,
  byWindowAction,
  sendAmplitudeEvent,
} from '@html/_common/utils';

export default function HeroImageGallery(tdpEventCategory, imageGalleryLightboxManager) {
  const $mainHeroImageWrapper = $('.js-ao-tour-hero-image-wrapper');
  const $mainHeroImage = $('.js-ao-tour-hero-image__main-image');
  const $previousImageButton = $('.js-ao-tour-hero-image__arrow-button--left');
  const $nextImageButton = $('.js-ao-tour-hero-image__arrow-button--right');
  const EVENT_CATEGORY = tdpEventCategory;

  let imageUrls = null;
  let currentImageIndex = 0;

  const prefetchedImageIndices = [];

  this.common = typeof sendGoogleEvent !== 'undefined' ? {
    prefetchImageFromSource,
    sendGoogleEvent,
    isObjectFitSupported,
    byWindowAction,
    sendAmplitudeEvent,
    lib: { init: libInit },
  } : common;

  this.initiate = (newImageUrls) => {
    const preparedImageUrls = this.setupImageUrls(newImageUrls);
    this.setupClickEvents();

    this.setupSwipeFunctionality();

    // prefetch second image
    this.prefetchImage(currentImageIndex + 1);
    this.initiateGalleryPopup(preparedImageUrls);
  };

  this.initiateGalleryPopup = (imageSources) => {
    if (window.self !== window.top) {
      // if we're embedded (e.g. b2b widgets), disable the gallery popup
      $('.ao-tour-hero-image-wrapper').css('cursor', 'default');
      return;
    }

    imageGalleryLightboxManager.createNewImageGallery(imageSources, {
      openElementSelector: '.js-ao-tour-hero-image-wrapper',
      startingIndex: () => this.getCurrentIndex(),
      lowerResolutionImageSize: '232x150',
      higherResolutionImageSize: '1500x800',
      googleEvents: {
        category: tdpEventCategory,
        eventGalleryName: 'Hero',
        actionVariations: [
          {
            className: 'js-ao-tour-hero-image-wrapper',
            action: 'With image click',
          },
        ],
      },
    });
  };

  this.setupImageUrls = (additionalImageUrls) => {
    const startingImageUrl = $mainHeroImageWrapper.data('starting-image-url');

    imageUrls = [startingImageUrl, ...additionalImageUrls];
    return imageUrls;
  };

  this.setupClickEvents = () => {
    $previousImageButton.on('click', () => {
      this.onPreviousImageButtonClick();
      this.sendImageInteractionGoogleEvent('Images', 'Previous', 'tdp_main_image_clicked_direction');
      this.common.sendAmplitudeEvent({
        ampEventName: 'tdp_hero_gallery_clicked',
        tdp_hero_gallery: 'Traditional',
        tdp_hero_gallery_type_clicked: 'Arrows',
      });
    });

    $nextImageButton.on('click', () => {
      this.onNextImageButtonClick();
      this.sendImageInteractionGoogleEvent('Images', 'Next', 'tdp_main_image_clicked_direction');
      this.common.sendAmplitudeEvent({
        ampEventName: 'tdp_hero_gallery_clicked',
        tdp_hero_gallery: 'Traditional',
        tdp_hero_gallery_type_clicked: 'Arrows',
      });
    });

    $mainHeroImageWrapper.on('click', () => {
      this.common.sendAmplitudeEvent({
        ampEventName: 'tdp_hero_gallery_clicked',
        tdp_hero_gallery: 'Traditional',
        tdp_hero_gallery_type_clicked: 'Operator Photo',
        tdp_hero_gallery_position: 'View all',
      });
    });
  };

  this.setupSwipeFunctionality = () => {
    this.common.lib.init('touch', () => {
      $mainHeroImageWrapper.swipeRight(() => {
        this.onNextImageButtonClick();
        this.sendImageInteractionGoogleEvent('Swipe image', 'Right', 'tdp_main_image_clicked_direction');
        this.common.sendAmplitudeEvent({
          ampEventName: 'tdp_hero_gallery_clicked',
          tdp_hero_gallery: 'Traditional',
          tdp_hero_gallery_type_clicked: 'Arrows',
        });
      });

      $mainHeroImageWrapper.swipeLeft(() => {
        this.onPreviousImageButtonClick();
        this.sendImageInteractionGoogleEvent('Swipe image', 'Left', 'tdp_main_image_clicked_direction');
        this.common.sendAmplitudeEvent({
          ampEventName: 'tdp_hero_gallery_clicked',
          tdp_hero_gallery: 'Traditional',
          tdp_hero_gallery_type_clicked: 'Arrows',
        });
      });
    });
  };

  this.onPreviousImageButtonClick = () => {
    currentImageIndex -= 1;

    if (this.isImageIndexOutOfBounds(currentImageIndex)) {
      currentImageIndex = (imageUrls.length - 1);
    }

    const indexOfImageToPrefetch = currentImageIndex - 1;
    this.prefetchImage(indexOfImageToPrefetch);

    const nextImageUrl = imageUrls[currentImageIndex];
    this.switchToImage(nextImageUrl);
  };

  this.onNextImageButtonClick = () => {
    currentImageIndex += 1;

    if (this.isImageIndexOutOfBounds(currentImageIndex)) {
      currentImageIndex = 0;
    }

    const indexOfImageToPrefetch = currentImageIndex + 1;
    this.prefetchImage(indexOfImageToPrefetch);

    const nextImageUrl = imageUrls[currentImageIndex];
    this.switchToImage(nextImageUrl);
  };

  this.prefetchImage = (indexOfImage) => {
    if (prefetchedImageIndices.indexOf(indexOfImage) === -1) {
      const src = imageUrls[indexOfImage];
      this.common.prefetchImageFromSource(src);
      prefetchedImageIndices.push(indexOfImage);
    }
  };

  this.sendImageInteractionGoogleEvent = (action, direction, ampEventName = null) => {
    this.common.sendGoogleEvent(
      EVENT_CATEGORY,
      action,
      direction,
      {
        ampEventName,
        tdp_main_image_clicked_direction: direction,
        tdp_main_image_modal: 'Close',
      },
    );
  };

  this.isImageIndexOutOfBounds = (imageIndex) => imageIndex > (imageUrls.length - 1) || imageIndex < 0;

  this.switchToImage = (imageUrl) => {
    /* IE does not support objectFit -> <img> tag gets replaced with
    * background-image on the outer wrapper.
    * Now we need to differentiate if we apply the switch to the <img>
    * or the wrapper.
    */
    if (this.common.isObjectFitSupported()) {
      if ($mainHeroImage.parent().hasClass('js-ao-tour-hero-image-wrapper')) {
        $mainHeroImage.attr('src', imageUrl);
      } else {
        const altText = $mainHeroImage.attr('alt');
        $mainHeroImageWrapper.html(`<img src='${imageUrl}' alt='${altText}' class='ao-tour-hero-image__main-image js-ao-tour-hero-image__main-image'>`);
      }
    } else {
      $mainHeroImageWrapper.css('background-image', `url("${imageUrl}")`);
    }
  };

  this.getCurrentIndex = () => currentImageIndex;
}
